import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { UserPlus, Trash2, Users, Shield, ShieldCheck, User, ArrowUpDown, ChevronUp, ChevronDown, Key, Eye, EyeOff, Building2, Warehouse, Save, Plus, Search } from 'lucide-react';
import { useSortableData } from '../hooks/useSortableData';
import Pagination from './Pagination';
const WAREHOUSE_OPTIONS = [
    'Primary',
    'SC_Ramona',
    'SC_DALLAS',
    'SC_FRESNO',
    'SC_Dubai',
    'DRMTLGY',
    'KITSCH_LA'
];

const ROLE_CONFIG = {
    admin: { icon: ShieldCheck, color: '#ef4444', label: 'Admin', bg: 'rgba(239, 68, 68, 0.1)' },
    manager: { icon: Shield, color: '#f59e0b', label: 'Manager', bg: 'rgba(245, 158, 11, 0.1)' },
    picker: { icon: User, color: '#3b82f6', label: 'Picker', bg: 'rgba(59, 130, 246, 0.1)' },
};

const Settings = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({ username: '', password: '', role: 'picker', warehouse: '' });
    const [formError, setFormError] = useState('');
    const [formLoading, setFormLoading] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [resetModalUser, setResetModalUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    // Mapping States
    const [clientMap, setClientMap] = useState({});
    const [warehouseMap, setWarehouseMap] = useState({});
    const [mapLoading, setMapLoading] = useState(true);
    const [isSavingMap, setIsSavingMap] = useState(false);
    const [mapError, setMapError] = useState('');

    // Pagination and Search for Client Map
    const [clientPage, setClientPage] = useState(1);
    const [clientSearchQuery, setClientSearchQuery] = useState('');
    const clientsPerPage = 15;

    const { items: sortedUsers, requestSort, sortConfig } = useSortableData(users);
    const getSortIcon = (key) => {
        if (!sortConfig || sortConfig.key !== key) return <ArrowUpDown size={14} style={{ display: 'inline', marginLeft: '4px', opacity: 0.3 }} />;
        return sortConfig.direction === 'ascending' ? <ChevronUp size={14} style={{ display: 'inline', marginLeft: '4px', color: 'var(--primary)' }} /> : <ChevronDown size={14} style={{ display: 'inline', marginLeft: '4px', color: 'var(--primary)' }} />;
    };

    const fetchUsers = useCallback(async () => {
        try {
            const res = await api.auth.listUsers();
            setUsers(res.data);
        } catch (err) {
            console.error('Failed to fetch users:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchMaps = useCallback(async () => {
        try {
            const [cRes, wRes] = await Promise.all([
                api.inventory.getClientMap(),
                api.inventory.getWarehouseMap()
            ]);
            console.log('Client Map Data:', cRes.data);
            console.log('Warehouse Map Data:', wRes.data);
            
            // Handle if data is actually nested under a property, or returned directly
            const clientData = cRes.data?.data || cRes.data || {};
            const warehouseData = wRes.data?.data || wRes.data || {};
            
            setClientMap(clientData);
            setWarehouseMap(warehouseData);
        } catch (err) {
            console.error('Failed to fetch maps:', err);
            setMapError(err.message || 'Error fetching maps');
        } finally {
            setMapLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
        fetchMaps();
    }, [fetchUsers, fetchMaps]);

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setFormError('');
        setFormLoading(true);

        try {
            await api.auth.createUser(formData);
            setFormData({ username: '', password: '', role: 'picker', warehouse: '' });
            setShowForm(false);
            fetchUsers();
        } catch (err) {
            setFormError(err.response?.data?.error || 'Failed to create user');
        } finally {
            setFormLoading(false);
        }
    };

    const handleDeleteUser = async (id) => {
        try {
            await api.auth.deleteUser(id);
            setDeleteConfirm(null);
            fetchUsers();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to delete user');
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (!newPassword || !resetModalUser) return;
        try {
            const res = await api.auth.resetPassword(resetModalUser.id, newPassword);
            alert(res.data?.message || 'Password reset successfully');
            setResetModalUser(null);
            setNewPassword('');
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to reset password');
        }
    };

    const handleSaveMap = async (type) => {
        setIsSavingMap(true);
        try {
            if (type === 'client') {
                await api.inventory.updateClientMap(clientMap);
                alert('Client mapping updated successfully');
            } else {
                await api.inventory.updateWarehouseMap(warehouseMap);
                alert('Warehouse mapping updated successfully');
            }
        } catch (err) {
            alert(`Failed to update ${type} mapping`);
        } finally {
            setIsSavingMap(false);
        }
    };

    const handleMapChange = (type, key, value) => {
        if (type === 'client') {
            setClientMap(prev => ({ ...prev, [key]: value }));
        } else {
            setWarehouseMap(prev => ({ ...prev, [key]: value }));
        }
    };

    const handleAddMapEntry = (type) => {
        const key = window.prompt(`Enter new ${type === 'client' ? 'Client ID' : 'Warehouse ID'}:`);
        if (!key) return;
        const val = window.prompt(`Enter Name for ${key}:`);
        if (!val) return;
        handleMapChange(type, key, val);
    };

    const handleRemoveMapEntry = (type, key) => {
        if (!window.confirm(`Remove mapping for ${key}?`)) return;
        if (type === 'client') {
            const newMap = { ...clientMap };
            delete newMap[key];
            setClientMap(newMap);
        } else {
            const newMap = { ...warehouseMap };
            delete newMap[key];
            setWarehouseMap(newMap);
        }
    };

    const RoleBadge = ({ role }) => {
        const config = ROLE_CONFIG[role] || ROLE_CONFIG.picker;
        const Icon = config.icon;
        return (
            <span className="settings-role-badge" style={{ backgroundColor: config.bg, color: config.color }}>
                <Icon size={14} />
                {config.label}
            </span>
        );
    };

    if (loading) return <div style={{ padding: '2rem' }}>Loading users...</div>;

    return (
        <div>
            {mapError && <div style={{ padding: '1rem', background: '#fee2e2', color: '#ef4444', margin: '1rem', borderRadius: '4px' }}>Error loading maps: {mapError}</div>}
            <div className="page-header">
                <div>
                    <h1><Users size={28} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} /> User Management</h1>
                    <p>Create, view, and manage user accounts and roles.</p>
                </div>
                <button
                    className="settings-add-btn"
                    onClick={() => setShowForm(!showForm)}
                >
                    <UserPlus size={18} />
                    {showForm ? 'Cancel' : 'Add User'}
                </button>
            </div>

            {showForm && (
                <div className="card settings-form-card">
                    <h3 className="card-title">Create New User</h3>
                    <form onSubmit={handleCreateUser} className="settings-form">
                        {formError && <div className="login-error" style={{ marginBottom: '1rem' }}>{formError}</div>}
                        <div className="settings-form-grid">
                            <div className="login-field">
                                <label>Username</label>
                                <input
                                    type="text"
                                    placeholder="Enter username"
                                    value={formData.username}
                                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="login-field">
                                <label>Password</label>
                                <input
                                    type="password"
                                    placeholder="Enter password"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="login-field">
                                <label>Role</label>
                                <select
                                    value={formData.role}
                                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                >
                                    <option value="admin">Admin</option>
                                    <option value="manager">Manager</option>
                                    <option value="picker">Picker</option>
                                </select>
                            </div>
                            {(formData.role === 'picker' || formData.role === 'manager') && (
                                <div className="login-field">
                                    <label>Warehouse</label>
                                    <select
                                        value={formData.warehouse}
                                        onChange={(e) => setFormData({ ...formData, warehouse: e.target.value })}
                                        required
                                    >
                                        <option value="">Select Warehouse</option>
                                        {WAREHOUSE_OPTIONS.map(w => (
                                            <option key={w} value={w}>{w}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                        <button type="submit" className="login-button" disabled={formLoading} style={{ marginTop: '1rem', maxWidth: '200px' }}>
                            {formLoading ? 'Creating...' : 'Create User'}
                        </button>
                    </form>
                </div>
            )}

            <div className="card">
                <div className="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th className="sortable" onClick={() => requestSort('username')}>Username {getSortIcon('username')}</th>
                                <th className="sortable" onClick={() => requestSort('role')}>Role {getSortIcon('role')}</th>
                                <th className="sortable" onClick={() => requestSort('warehouse')}>Warehouse {getSortIcon('warehouse')}</th>
                                <th className="sortable" onClick={() => requestSort('created_at')}>Created {getSortIcon('created_at')}</th>
                                <th style={{ width: '80px' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedUsers.map(u => (
                                <tr key={u.id}>
                                    <td style={{ fontWeight: 600 }}>{u.username}</td>
                                    <td><RoleBadge role={u.role} /></td>
                                    <td>{u.warehouse || '—'}</td>
                                    <td>{new Date(u.created_at).toLocaleDateString()}</td>
                                    <td>
                                        {deleteConfirm === u.id ? (
                                            <div style={{ display: 'flex', gap: '0.25rem' }}>
                                                <button
                                                    className="settings-delete-confirm"
                                                    onClick={() => handleDeleteUser(u.id)}
                                                    title="Confirm delete"
                                                >
                                                    Yes
                                                </button>
                                                <button
                                                    className="settings-delete-cancel"
                                                    onClick={() => setDeleteConfirm(null)}
                                                    title="Cancel"
                                                >
                                                    No
                                                </button>
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                <button
                                                    className="settings-reset-btn"
                                                    onClick={() => { setResetModalUser(u); setNewPassword(''); }}
                                                    title="Reset password"
                                                >
                                                    <Key size={16} />
                                                </button>
                                                <button
                                                    className="settings-delete-btn"
                                                    onClick={() => setDeleteConfirm(u.id)}
                                                    title="Delete user"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {users.length === 0 && (
                                <tr><td colSpan="5" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '2rem' }}>No users found.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {resetModalUser && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div className="card" style={{ padding: '2rem', width: '400px', maxWidth: '90%' }}>
                        <h3 style={{ marginTop: 0, marginBottom: '1.5rem', color: 'var(--text-main)' }}>Reset Password for {resetModalUser.username}</h3>
                        <form onSubmit={handleResetPassword}>
                            <div className="login-field">
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-muted)' }}>New Password</label>
                                <div className="password-input-wrapper">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="Enter new password"
                                        required
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #e2e8f0', outline: 'none' }}
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle-btn"
                                        onClick={() => setShowPassword(!showPassword)}
                                        title={showPassword ? "Hide password" : "Show password"}
                                        style={{ right: '0.5rem' }}
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                                <button type="submit" className="login-button" style={{ flex: 1 }}>Reset Password</button>
                                <button type="button" onClick={() => { setResetModalUser(null); setShowPassword(false); }} style={{ flex: 1, padding: '0.75rem', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', backgroundColor: 'transparent', color: 'var(--text-main)', fontWeight: 500 }}>Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Client Mapping Section */}
            <div className="page-header" style={{ marginTop: '3rem' }}>
                <div>
                    <h1><Building2 size={28} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} /> Client Management</h1>
                    <p>Map ShipHero Client IDs to friendly display names.</p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <div style={{ position: 'relative' }}>
                        <Search size={18} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                        <input
                            type="text"
                            placeholder="Search ID or Name..."
                            value={clientSearchQuery}
                            onChange={(e) => { setClientSearchQuery(e.target.value); setClientPage(1); }}
                            style={{ padding: '0.5rem 0.5rem 0.5rem 2.2rem', borderRadius: '6px', border: '1px solid #e2e8f0', width: '250px' }}
                        />
                    </div>
                    <button className="settings-add-btn" onClick={() => handleAddMapEntry('client')}>
                        <Plus size={18} /> Add Mapping
                    </button>
                    <button className="settings-add-btn" style={{ backgroundColor: '#10b981' }} onClick={() => handleSaveMap('client')} disabled={isSavingMap}>
                        <Save size={18} /> {isSavingMap ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>

            <div className="card">
                <div className="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th>ShipHero Client ID</th>
                                <th>Display Name</th>
                                <th style={{ width: '80px' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {mapLoading ? (
                                <tr><td colSpan="3" style={{ textAlign: 'center', padding: '1rem' }}>Loading maps...</td></tr>
                            ) : Object.keys(clientMap || {}).length === 0 ? (
                                <tr><td colSpan="3" style={{ textAlign: 'center', padding: '1rem', color: 'gray' }}>No clients found.</td></tr>
                            ) : Object.entries(clientMap || {})
                                .filter(([id, name]) => {
                                    const strId = String(id).toLowerCase();
                                    const strName = String(name).toLowerCase();
                                    const q = clientSearchQuery.toLowerCase();
                                    return strId.includes(q) || strName.includes(q);
                                })
                                .slice((clientPage - 1) * clientsPerPage, clientPage * clientsPerPage)
                                .map(([id, name]) => (
                                    <tr key={id}>
                                        <td style={{ fontWeight: 600, color: 'var(--text-muted)' }}>{id}</td>
                                        <td>
                                            <input
                                                type="text"
                                                value={name}
                                                onChange={(e) => handleMapChange('client', id, e.target.value)}
                                                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #e2e8f0' }}
                                            />
                                        </td>
                                        <td>
                                            <button className="settings-delete-btn" onClick={() => handleRemoveMapEntry('client', id)}>
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Controls */}
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '1rem', borderTop: '1px solid #f1f5f9' }}>
                    <Pagination 
                        currentPage={clientPage} 
                        totalPages={Math.max(1, Math.ceil(Object.entries(clientMap || {}).filter(([id, name]) => String(id).toLowerCase().includes(clientSearchQuery.toLowerCase()) || String(name).toLowerCase().includes(clientSearchQuery.toLowerCase())).length / clientsPerPage))} 
                        setCurrentPage={setClientPage} 
                    />
                </div>
            </div>

            {/* Warehouse Mapping Section */}
            <div className="page-header" style={{ marginTop: '3rem' }}>
                <div>
                    <h1><Warehouse size={28} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} /> Warehouse Management</h1>
                    <p>Map ShipHero Warehouse IDs to friendly display names.</p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="settings-add-btn" onClick={() => handleAddMapEntry('warehouse')}>
                        <Plus size={18} /> Add Mapping
                    </button>
                    <button className="settings-add-btn" style={{ backgroundColor: '#10b981' }} onClick={() => handleSaveMap('warehouse')} disabled={isSavingMap}>
                        <Save size={18} /> {isSavingMap ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>

            <div className="card" style={{ marginBottom: '4rem' }}>
                <div className="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th>ShipHero Warehouse ID</th>
                                <th>Display Name</th>
                                <th style={{ width: '80px' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {mapLoading ? (
                                <tr><td colSpan="3" style={{ textAlign: 'center', padding: '1rem' }}>Loading maps...</td></tr>
                            ) : Object.keys(warehouseMap || {}).length === 0 ? (
                                <tr><td colSpan="3" style={{ textAlign: 'center', padding: '1rem', color: 'gray' }}>No warehouses found.</td></tr>
                            ) : Object.entries(warehouseMap || {}).map(([id, name]) => (
                                <tr key={id}>
                                    <td style={{ fontWeight: 600, color: 'var(--text-muted)', fontSize: '0.75rem' }}>{id}</td>
                                    <td>
                                        <input
                                            type="text"
                                            value={name}
                                            onChange={(e) => handleMapChange('warehouse', id, e.target.value)}
                                            style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #e2e8f0' }}
                                        />
                                    </td>
                                    <td>
                                        <button className="settings-delete-btn" onClick={() => handleRemoveMapEntry('warehouse', id)}>
                                            <Trash2 size={16} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default Settings;
